import { useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RESOURCES } from "@/admin/config";
import ResourceManager from "@/admin/ResourceManager";
import Enquiries from "@/admin/Enquiries";
import { IMG_HERO } from "@/lib/images";

const TABS = [...Object.keys(RESOURCES), "enquiries"];

function Login({ denied }) {
  const { login, logout, user } = useAuth();
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#fcfbfa]" data-testid="admin-login-page">
      <div className="hidden md:block relative"><img src={IMG_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" /></div>
      <div className="flex flex-col justify-center px-8 sm:px-16 py-16">
        <Link to="/" className="text-sm text-[#5a665e] inline-flex items-center gap-2" data-testid="admin-back-to-site"><ArrowLeft size={14} /> Back to site</Link>
        <p className="eyebrow mt-12">Admin</p>
        <h1 className="mt-4 text-4xl sm:text-5xl text-[#1a261f]">Content desk</h1>
        {denied ? (
          <>
            <p className="mt-6 text-[#5a665e] max-w-sm" data-testid="admin-denied-message">
              {user?.email} is signed in but doesn't have admin access. Ask the site owner to grant it.
            </p>
            <button onClick={logout} className="mt-8 self-start border border-[#e2dcd4] px-6 py-3 text-sm" data-testid="admin-denied-logout">Sign out</button>
          </>
        ) : (
          <>
            <p className="mt-6 text-[#5a665e] max-w-sm">Manage galleries, articles, events, regional notices and enquiries. The first account to sign in becomes the owner.</p>
            <button onClick={login} className="mt-8 self-start bg-[#1f402b] text-white px-7 py-3.5 text-sm hover:bg-[#2c5a3d]" data-testid="admin-login-button">
              Sign in with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("galleries");

  if (loading) return <div className="min-h-screen bg-[#fcfbfa]" />;
  if (!user) return <Login />;
  if (user.role !== "admin") return <Login denied />;

  return (
    <div className="min-h-screen bg-[#f7f4ef]" data-testid="admin-dashboard-view">
      <header className="bg-[#fcfbfa] border-b border-[#e2dcd4]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display text-xl text-[#1f402b]" data-testid="admin-home-link"><img src="/logo-badge.png" alt="" className="w-8 h-8" />kiwibares<span className="text-[#c5903b] font-mono-k text-xs">.nz</span> <span className="eyebrow ml-2">admin</span></Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-[#5a665e]" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-[#5a665e] hover:text-[#1f402b]" data-testid="admin-logout-button"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-10 [&>*]:min-w-0">
        <nav className="flex md:flex-col gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`text-left whitespace-nowrap px-3 py-2 text-sm transition-colors ${tab === t ? "bg-[#1f402b] text-white" : "text-[#3c4a41] hover:bg-[#ece6dc]"}`}>
              {RESOURCES[t]?.label || "Enquiries"}
            </button>
          ))}
        </nav>
        <section>{tab === "enquiries" ? <Enquiries /> : <ResourceManager key={tab} name={tab} />}</section>
      </div>
    </div>
  );
}
